import { graphqlRequest } from "./client";
import { CHECK_FIELDS, type CheckItem, type RubricQuestionShape, type Track } from "./types";

export interface AssessmentView {
  submitted: boolean;
  url?: string | null;
  structPass: boolean;
  structChecks: CheckItem[];
  selfCheckConfirmed: boolean;
  selfCheck: string[];
  round: number;
  reviewsDone: number;
  reviewsAssigned: number;
  reviewsReceived: number;
  reviewsNeeded: number;
  qualityPct?: number | null;
  verdictKey: string;
  verdictTitle: string;
  verdictText: string;
  pass: boolean;
  canResubmit: boolean;
  perQuestion: { key: string; q: string; w: number; avg: number }[];
  checklist: {
    key: string;
    t: string;
    youSaid: boolean;
    peersYes: number;
    peersTotal: number;
    disagree: boolean;
  }[];
}

/** An anonymous portfolio to review. There is deliberately no field saying whose it is. */
export interface ReviewTask {
  assignmentId: string;
  track: Track;
  url: string;
  rubric: RubricQuestionShape[];
}

export const PASS_MARK = 70;

export const ASSESSMENT_FIELDS = `submitted url structPass structChecks { ${CHECK_FIELDS} } selfCheckConfirmed selfCheck round
  reviewsDone reviewsAssigned reviewsReceived reviewsNeeded qualityPct verdictKey verdictTitle verdictText pass canResubmit
  perQuestion { key q w avg } checklist { key t youSaid peersYes peersTotal disagree }`;

/** Step 4 readiness assessment status and result. */
export async function fetchAssessment(): Promise<AssessmentView> {
  return (
    await graphqlRequest<{ getAssessment: AssessmentView }>(
      `{ getAssessment { ${ASSESSMENT_FIELDS} } }`,
    )
  ).getAssessment;
}

/** Enter the peer-review pool with the Step 2 portfolio. */
export async function enterAssessment(): Promise<AssessmentView> {
  return (
    await graphqlRequest<{ enterAssessment: AssessmentView }>(
      `mutation { enterAssessment { ${ASSESSMENT_FIELDS} } }`,
    )
  ).enterAssessment;
}

/** Confirm the honest self-checklist. */
export async function confirmSelfCheck(keys: string[]): Promise<AssessmentView> {
  return (
    await graphqlRequest<{ confirmSelfCheck: AssessmentView }>(
      `mutation($keys: [String!]!) { confirmSelfCheck(keys: $keys) { ${ASSESSMENT_FIELDS} } }`,
      { variables: { keys } },
    )
  ).confirmSelfCheck;
}

/** The next portfolio to review, or null when there is none. */
export async function fetchNextReview(): Promise<ReviewTask | null> {
  return (
    await graphqlRequest<{ getNextReview: ReviewTask | null }>(
      `{ getNextReview { assignmentId track url rubric { key w q prompt lo hi } } }`,
    )
  ).getNextReview;
}

/** Submit an assigned review. */
export async function submitReview(
  assignmentId: string,
  scores: Record<string, number>,
  checklist: string[],
): Promise<AssessmentView> {
  return (
    await graphqlRequest<{ submitReview: AssessmentView }>(
      `mutation($input: SubmitReviewInput!) { submitReview(input: $input) { ${ASSESSMENT_FIELDS} } }`,
      {
        variables: {
          input: {
            assignmentId,
            checklist,
            scores: Object.entries(scores).map(([key, score]) => ({ key, score })),
          },
        },
      },
    )
  ).submitReview;
}

/** Resubmit an improved portfolio for a fresh round. */
export async function resubmitPortfolio(
  url: string,
  confirmImproved: boolean,
): Promise<AssessmentView> {
  return (
    await graphqlRequest<{ resubmitPortfolio: AssessmentView }>(
      `mutation($input: ResubmitInput!) { resubmitPortfolio(input: $input) { ${ASSESSMENT_FIELDS} } }`,
      { variables: { input: { url, confirmImproved } } },
    )
  ).resubmitPortfolio;
}
